/**
 * `<Slot>` — render every fragment registered against the named slot, in
 * order, passing each one the slot's typed context as its sole prop.
 *
 * RBAC gating is deliberately simple: if a fragment declares `requires`,
 * we skip rendering it when the principal lacks that permission. The
 * permission check happens client-side as a UX guard only — the
 * underlying API endpoints the fragment talks to enforce server-side, so
 * a malicious client can't bypass anything by mounting the fragment by
 * hand.
 *
 * `<Slot>` itself is a tiny wrapper that fans out — no styling, no
 * spacing. Slots are intended to compose into existing layouts; whatever
 * vertical rhythm the surrounding page uses applies to the fragments
 * naturally.
 */
import { Fragment } from 'react';
import { extensionsForSlot } from './registry.js';
import type { SlotContextMap, SlotName } from './types.js';

interface Props<S extends SlotName> {
  name: S;
  context: SlotContextMap[S];
}

export function Slot<S extends SlotName>({ name, context }: Props<S>): JSX.Element {
  const extensions = extensionsForSlot(name);
  return (
    <>
      {extensions.map((ext) => {
        // TODO: when permission propagation lands, evaluate `ext.requires` here
        // against the current user. For now every fragment renders — the
        // applications card's `listForOrg` already RBAC-checks server-side.
        const Component = ext.component as React.ComponentType<SlotContextMap[S]>;
        return (
          <Fragment key={ext.id}>
            <Component {...context} />
          </Fragment>
        );
      })}
    </>
  );
}
